/** Quill / contenteditable often emit NBSP as a char or `&nbsp;` entity. */
const normalizeNbsp = (input: string): string =>
  input.replace(/&nbsp;/gi, ' ').replace(/\u00A0/g, ' ');

/**
 * Whitespace / punctuation cleanup for free-text fields:
 * - replace non-printing characters with spaces
 * - remove spaces immediately before commas and full stops
 * - ensure a single space after commas when more text follows
 * - collapse multiple spaces to one
 * - trim ends (per line; newlines preserved)
 *
 * ex: " Rose Water Refreshes Dull Looking Skin ,   Soothing, Toning , Hydration , And Antioxidiant "
 *  → "Rose Water Refreshes Dull Looking Skin, Soothing, Toning, Hydration, And Antioxidiant"
 */
export const cleanFieldText = (input: string): string => {
  if (typeof input !== 'string') return input;
  if (!input) return input;

  return normalizeNbsp(input)
    .split('\n')
    .map((line) => {
      // eslint-disable-next-line no-control-regex
      let cleaned = line.replace(/[\x00-\x1F\x7F-\x9F]/g, ' ');
      // "Skin ," / "end ." → "Skin," / "end."
      cleaned = cleaned.replace(/\s+([,.])/g, '$1');
      // "Skin,Soothing" → "Skin, Soothing" (do not auto-space after "." — breaks 2.5, e.g.)
      cleaned = cleaned.replace(/,([^\s])/g, ', $1');
      cleaned = cleaned.replace(/\s+/g, ' ').trim();
      return cleaned;
    })
    .join('\n');
};

/**
 * Same spacing rules for HTML rich-text, applied only to text between tags
 * so markup is preserved. Normalizes Quill `&nbsp;` first.
 */
export const cleanRichTextHtml = (html: string): string => {
  if (typeof html !== 'string' || !html) return html;
  const normalized = normalizeNbsp(html);
  if (!/[,.\s]/.test(normalized)) return html;

  return normalized.replace(
    /(^|>)([^<>]*)(<|$)/g,
    (_full, open: string, text: string, close: string) => {
      if (!text || !/[,.\s]/.test(text)) return `${open}${text}${close}`;

      const leading = text.match(/^\s*/)?.[0] ?? '';
      const trailing = text.match(/\s*$/)?.[0] ?? '';
      let mid = text.slice(leading.length, text.length - trailing.length);
      mid = mid
        .replace(/\s+([,.])/g, '$1')
        .replace(/,([^\s])/g, ', $1')
        .replace(/\s+/g, ' ');

      const lead = leading.replace(/[^\S\n]+/g, ' ');
      const trail = trailing.replace(/[^\S\n]+/g, ' ');
      return `${open}${lead}${mid}${trail}${close}`;
    },
  );
};

const looksLikeHtml = (value: string): boolean =>
  /<\/?[a-z][\s\S]*>/i.test(value);

/** Clean plain text or rich-text HTML string values. */
export const cleanRemedyTextValue = (input: string): string => {
  if (typeof input !== 'string' || !input) return input;
  return looksLikeHtml(input)
    ? cleanRichTextHtml(input)
    : cleanFieldText(input);
};

/**
 * Deep-clean string fields on a remedy payload. Skips URLs, ids, and binary/media.
 */
export const cleanRemedyPayloadStrings = <T>(payload: T): T => {
  const skipKey = (key: string): boolean => {
    const k = key.toLowerCase();
    return (
      k === 'id' ||
      k.endsWith('_id') ||
      k.endsWith('_ids') ||
      k.includes('url') ||
      k.includes('image') ||
      k.includes('video') ||
      k.includes('file') ||
      k.includes('session') ||
      k.includes('token') ||
      k.includes('version') ||
      k === 'type' ||
      k === 'visibility_scope' ||
      k === 'status'
    );
  };

  const walk = (value: unknown, key?: string): unknown => {
    if (value == null) return value;
    if (typeof File !== 'undefined' && value instanceof File) return value;
    if (typeof Blob !== 'undefined' && value instanceof Blob) return value;
    if (typeof FileList !== 'undefined' && value instanceof FileList)
      return value;

    if (typeof value === 'string') {
      if (key && skipKey(key)) return value;
      if (/^https?:\/\//i.test(value)) return value;
      return cleanRemedyTextValue(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) => walk(item, key));
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(obj)) {
        out[k] = skipKey(k) ? v : walk(v, k);
      }
      return out;
    }

    return value;
  };

  return walk(payload) as T;
};

/**
 * Utility to clean and format strings according to user rules:
 * - Replace non-printing characters with spaces
 * - Remove spaces before commas / full stops
 * - Replace multiple white spaces with single space and trim
 * - When a word is all capital, leave it as is
 * - When it is a single letter keep it lowercase
 * - Otherwise Capitalize the word
 */
export const cleanInputString = (input: string): string => {
  if (typeof input !== 'string') return input;
  if (!input) return input;

  return input
    .split('\n')
    .map((line) => {
      const cleaned = cleanFieldText(line);
      if (!cleaned) return cleaned;

      // Format capitalization (per word)
      const words = cleaned.split(' ');
      const formattedWords = words.map((word) => {
        // If it's all caps (and contains letters), leave as is
        if (word === word.toUpperCase() && /[A-Za-z]/.test(word)) {
          return word;
        }

        // If it's a single letter, make it lowercase (checking only alphabet characters)
        const alphaOnly = word.replace(/[^A-Za-z]/g, '');
        if (alphaOnly.length === 1) {
          return word.toLowerCase();
        }

        // Capitalize first letter, lowercase the rest
        const firstAlphaMatch = word.match(/[A-Za-z]/);
        if (firstAlphaMatch) {
          const idx = firstAlphaMatch.index!;
          return (
            word.substring(0, idx) +
            word.charAt(idx).toUpperCase() +
            word.substring(idx + 1).toLowerCase()
          );
        }

        return word;
      });

      return formattedWords.join(' ');
    })
    .join('\n');
};
